import { AfterViewInit, ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { isNullOrUndefined } from '@sla-ng/core';
import { Moment } from 'moment';
import { debounceTime, distinctUntilChanged, Observable, Subject, takeUntil } from 'rxjs';
import { map } from 'rxjs/operators';
import { IArticle } from '../../../models/article.model';
import { IPurchase } from '../../../models/purchase.model';
import { TransactionalDataFacade } from '../../../services/facades/external-data/transactional-data-facade';
import { ITransactionalDataState } from '../../../services/facades/external-data/transactional-data.state.model';

@Component({
    templateUrl: './purchase.page.html',
    styleUrls: [ './purchase.page.css' ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PurchasePage implements OnInit, AfterViewInit, OnDestroy {

    @ViewChild(MatSort)
    public sort: MatSort;

    @ViewChild(MatPaginator)
    public paginator: MatPaginator;

    public dataSource: MatTableDataSource<IPurchase> = new MatTableDataSource<IPurchase>([]);
    public purchaseState$: Observable<ITransactionalDataState>;
    public datefilterFormGroup: FormGroup = new FormGroup({ start: new FormControl<Moment>(null), end: new FormControl<Moment>(null) });
    private destroy$: Subject<void> = new Subject();

    constructor(private myTransactionalDataFacade: TransactionalDataFacade) {
    }

    public ngOnInit(): void {
        this.purchaseState$ = this.myTransactionalDataFacade.subState();
        this.purchaseState$
            .pipe(
                takeUntil(this.destroy$),
                map((s: ITransactionalDataState) => s.purchases),
                distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)))
            .subscribe((data: IPurchase[]) => {
                this.dataSource.data = data;
            });
        this.datefilterFormGroup.valueChanges.pipe(debounceTime(500)).subscribe((daterange: { start: Moment, end: Moment }) => {
            if (!isNullOrUndefined(daterange.start) && !isNullOrUndefined(daterange.end)) {
                this.myTransactionalDataFacade.setDateRange(daterange);
                void this.myTransactionalDataFacade.fetchPurchases();
            }
        });

        this.setupFilterAndSorting();

        // Initialize the datepicker with the daterange from the facade
        const state: ITransactionalDataState = this.myTransactionalDataFacade.getStateSnapshot();
        this.datefilterFormGroup.setValue(state.selectedDateRange);
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        // Remove Purchases from memory
        this.myTransactionalDataFacade.clearPurchases();
    }

    public ngAfterViewInit(): void {
        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;
    }

    public applyFilter(filterValue: string): void {
        filterValue = filterValue.trim(); // Remove whitespace
        filterValue = filterValue.toLowerCase(); // MatTableDataSource defaults to lowercase matches
        this.dataSource.filter = filterValue;
    }

    private setupFilterAndSorting(): void {
        this.purchaseState$
            .pipe(
                takeUntil(this.destroy$),
                map((s: ITransactionalDataState) => s.articleLookup),
                distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)))
            .subscribe((articleLookup: Record<number, IArticle>) => {
                this.dataSource.sortingDataAccessor = (purchase: IPurchase, tableColumnName: string): string | number => {
                    switch (tableColumnName) {
                        case 'article':
                            return articleLookup[ purchase.articleId ]?.name;
                        default:
                            return purchase[ tableColumnName ];
                    }
                };

                this.dataSource.filterPredicate = (purchase: IPurchase, filter: string): boolean => {
                    filter = filter.trim().toLowerCase();
                    const purchaseArticle: IArticle = articleLookup[ purchase.articleId ];

                    return purchaseArticle?.name.trim().toLowerCase().indexOf(filter) !== -1
                        || purchaseArticle?.articleNr.trim().toLowerCase().indexOf(filter) !== -1;
                };
            });


    }

}
